/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from "react";
import { useTheme } from "../../theme/ThemeContext";

/**
 * A themed toggle switch component.
 * 
 * Props:
 * - checked (bool): whether the toggle is on
 * - onChange (func): callback when toggled
 * - label (string): text label (optional)
 * - disabled (bool): if true, toggle is disabled
 * - onLabel (string): label for ON state (default: "On")
 * - offLabel (string): label for OFF state (default: "Off")
 */
export default function ThemedToggle({ checked, onChange, disabled, onLabel = "On", offLabel = "Off", style }) {
    const { theme } = useTheme();

    const handleToggle = () => {
        if (!disabled && onChange) {
            onChange(!checked);
        }
    };

    return (
        <div
            style={{ display: "flex", alignItems: "center", gap: 12, opacity: disabled ? 0.6 : 1, ...style }}
            onClick={handleToggle}
        >
            <div
                style={{
                    position: "relative",
                    width: 44,
                    height: 24,
                    borderRadius: 20,
                    background: checked ? theme.primary : "#cbd5e1",
                    transition: "background 0.3s ease",
                    cursor: disabled ? "not-allowed" : "pointer",
                    flexShrink: 0
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        top: 3,
                        left: checked ? 23 : 3,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: "#fff",
                        transition: "left 0.3s ease",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                    }}
                />
            </div>
            <span style={{ fontSize: "0.95rem", color: theme.text, fontWeight: 500, cursor: disabled ? "not-allowed" : "pointer" }}>
                {checked ? onLabel : offLabel}
            </span>
        </div>
    );
}
